import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
  ListItemSecondaryAction,
  CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import DevicesDataService from "../../services/DeviceService";
import SportsTicketsService from "../../services/SportsTicketsService";
import dayjs from "dayjs";
import AppBarBasic from "../layout/AppBar";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Tooltip } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import UserDataService from "../../services/UserService";
import DeviceUsersService from "../../services/DeviceUsersService";
import MqttService from "../../services/MqttService";
import { FixedSizeGrid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import PrintQRTickets from "./PrintQRTickets";

const drawerWidth = 280;

const BlackTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "black",
    fontSize: 13,
    padding: "8px 12px",
    borderRadius: 4,
  },
  arrow: {
    color: "black",
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexGrow: 1,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    background: "linear-gradient(#006987 0%, #003544 100%)",
    border: "none",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
    height: "100%",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: "16px",
  },
  ticketsList: {
    overflow: "auto",
    maxHeight: "410px",
    marginTop: theme.spacing(2),
    paddingRight: "5px",
    marginRight: "-5px",
    position: "relative",
    width: "100%",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
  listItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: "72px",
    marginRight: "5px",
    paddingRight: "96px", // Space for two action buttons
    width: "auto",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  deleteButton: {
    color: theme.palette.error.light,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
  viewButton: {
    color: theme.palette.primary.light,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  listHeader: {
    display: "flex",
    flexDirection: "column",
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  datePicker: {
    width: "100%",
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  qrCodeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  qrCode: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
  },
  ticketInfo: {
    marginBottom: theme.spacing(2),
  },
  ticketGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  ticketCard: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  usedTicket: {
    opacity: 0.6,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
}));

// View Tickets Dialog section - fixing potential JSX syntax issues
const ViewTicketsDialog = ({ open, onClose, ticketData, classes }) => {
  if (!ticketData) return null;

  // State to track if we should show all tickets or use virtualization
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Calculate number of columns based on available width (we'll use 3 columns as default)
  const COLUMN_COUNT = 3;
  const ITEM_WIDTH = 200;
  const ITEM_HEIGHT = 280;

  // Get event name safely without optional chaining
  const eventName =
    ticketData && ticketData.sportsTicket
      ? ticketData.sportsTicket.event_name
      : "";

  // Render a single ticket cell (used by both virtualized and non-virtualized views)
  const renderTicketCell = (ticket) => (
    <Paper
      className={`${classes.ticketCard} ${
        ticket.is_used ? classes.usedTicket : ""
      }`}
    >
      <Typography variant="body1" gutterBottom>
        Билет #{ticket.ticket_number}
      </Typography>
      <QRCodeSVG
        value={ticket.qr_code}
        size={150}
        level="H"
        includeMargin={true}
      />
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ marginTop: "8px" }}
      >
        {ticket.is_used ? "Използван" : "Неизползван"}
      </Typography>
    </Paper>
  );

  // Render a loading cell
  const renderLoadingCell = (index) => (
    <Paper className={classes.ticketCard}>
      <Typography variant="body1" gutterBottom>
        Зареждане...
      </Typography>
      <div
        style={{
          width: 150,
          height: 150,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={50} />
      </div>
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ marginTop: "8px" }}
      >
        Зареждане на билет
      </Typography>
    </Paper>
  );

  // Cell renderer for virtualized grid
  const Cell = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * COLUMN_COUNT + columnIndex;
    const tickets = ticketData.individualTickets || [];

    if (index >= tickets.length) {
      return <div style={style}></div>; // Empty cell
    }

    return (
      <div
        style={{
          ...style,
          padding: "8px",
          boxSizing: "border-box",
        }}
      >
        {renderTicketCell(tickets[index])}
      </div>
    );
  };

  // Loading Cell renderer for virtualized grid
  const LoadingCell = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * COLUMN_COUNT + columnIndex;

    if (index >= (ticketData.totalTickets || 10)) {
      return <div style={style}></div>; // Empty cell
    }

    return (
      <div
        style={{
          ...style,
          padding: "8px",
          boxSizing: "border-box",
        }}
      >
        {renderLoadingCell(index)}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{eventName}</DialogTitle>
        <DialogContent>
          <div className={classes.ticketInfo}>
            <Typography variant="h6">
              {ticketData.sportsTicket.home_team} vs{" "}
              {ticketData.sportsTicket.away_team}
            </Typography>
            <Typography variant="body1">
              Дата:{" "}
              {dayjs(ticketData.sportsTicket.event_date).format(
                "DD/MM/YYYY HH:mm"
              )}
            </Typography>
            <Typography variant="body1">
              Локация: {ticketData.sportsTicket.event_location}
            </Typography>
            <Typography variant="body1">
              Общ брой билети: {ticketData.totalTickets || 0}
            </Typography>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography variant="h6">Индивидуални билети</Typography>
            <div>
              {ticketData.individualTickets && ticketData.totalTickets > 100 && (
                <Button
                  color="primary"
                  onClick={() => setShowAllTickets(!showAllTickets)}
                  style={{ marginRight: "8px" }}
                >
                  {showAllTickets
                    ? "Използвай виртуализация"
                    : "Покажи всички билети"}
                </Button>
              )}
              {ticketData.individualTickets && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setPrintDialogOpen(true)}
                >
                  Принтирай билети
                </Button>
              )}
            </div>
          </div>

          <div style={{ height: "600px", width: "100%" }}>
            {!ticketData.individualTickets ? (
              // Show loading indicators when tickets are still loading
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeGrid
                    columnCount={COLUMN_COUNT}
                    columnWidth={width / COLUMN_COUNT}
                    height={height}
                    rowCount={Math.ceil(
                      (ticketData.totalTickets || 10) / COLUMN_COUNT
                    )}
                    rowHeight={ITEM_HEIGHT}
                    width={width}
                  >
                    {LoadingCell}
                  </FixedSizeGrid>
                )}
              </AutoSizer>
            ) : showAllTickets ? (
              // Show all tickets without virtualization (may be slow for large datasets)
              <div className={classes.ticketGrid}>
                {ticketData.individualTickets.map((ticket) => (
                  <div key={ticket.id}>{renderTicketCell(ticket)}</div>
                ))}
              </div>
            ) : (
              // Show virtualized grid for better performance
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeGrid
                    columnCount={COLUMN_COUNT}
                    columnWidth={width / COLUMN_COUNT}
                    height={height}
                    rowCount={Math.ceil(
                      ticketData.individualTickets.length / COLUMN_COUNT
                    )}
                    rowHeight={ITEM_HEIGHT}
                    width={width}
                  >
                    {Cell}
                  </FixedSizeGrid>
                )}
              </AutoSizer>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Затвори
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print QR Tickets Dialog */}
      <PrintQRTickets
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        ticketData={ticketData}
      />
    </>
  );
};

const SportsTicketsPage = () => {
  const classes = useStyles();

  // State management
  const [devices, setDevices] = useState([]);
  const [sportsTickets, setSportsTickets] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Form state
  const [formData, setFormData] = useState({
    event_name: "",
    home_team: "",
    away_team: "",
    event_date: new Date(),
    event_location: "",
    ticket_count: 10,
    selectedDevices: [],
    event_duration: 5, // Default duration in hours
  });

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    ticketId: null,
    ticketName: "",
    userCount: 0,
    userSaps: [],
    hasMoreUsers: false,
    deviceNames: [],
  });

  const [viewDialog, setViewDialog] = useState({
    open: false,
    ticketData: null,
  });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [devicesRes, ticketsRes] = await Promise.all([
          DevicesDataService.adminAll(),
          SportsTicketsService.getAll(),
        ]);

        setDevices(devicesRes.data);
        setSportsTickets(ticketsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        showSnackbar("Error loading data", "error");
      }
    };

    fetchData();
  }, []);

  // Ensure selectedDevices is always an array
  useEffect(() => {
    if (!Array.isArray(formData.selectedDevices)) {
      setFormData((prev) => ({
        ...prev,
        selectedDevices: [],
      }));
    }
  }, [formData.selectedDevices]);

  // Log when selectedDevices changes
  useEffect(() => {
    console.log("selectedDevices changed:", formData.selectedDevices);
  }, [formData.selectedDevices]);

  // Helper function for snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form data at submission:", formData);

    // Ensure selectedDevices is an array and has at least one device
    const selectedDevices = Array.isArray(formData.selectedDevices)
      ? formData.selectedDevices
      : [];

    console.log("Form submission - selectedDevices:", selectedDevices);
    console.log(
      "Form submission - selectedDevices length:",
      selectedDevices.length
    );
    console.log(
      "Form submission - selectedDevices is array:",
      Array.isArray(selectedDevices)
    );

    // Check if we have at least one device selected
    if (selectedDevices.length === 0) {
      console.log("No devices selected");
      showSnackbar("Моля, изберете поне едно устройство", "error");
      return;
    }

    // Log the first device to verify it's a valid object
    if (selectedDevices.length > 0) {
      console.log("First selected device:", selectedDevices[0]);
      console.log("First selected device ID:", selectedDevices[0].id);
    }

    // Check other required fields
    if (
      !formData.event_name ||
      !formData.home_team ||
      !formData.away_team ||
      !formData.event_date ||
      !formData.event_location ||
      !formData.ticket_count ||
      !formData.event_duration
    ) {
      console.log("Validation failed. Missing fields:", {
        event_name: !formData.event_name,
        home_team: !formData.home_team,
        away_team: !formData.away_team,
        event_date: !formData.event_date,
        event_location: !formData.event_location,
        ticket_count: !formData.ticket_count,
        event_duration: !formData.event_duration,
      });
      showSnackbar("Моля попълнете всички полета", "error");
      return;
    }

    try {
      // Show processing message for large ticket counts
      if (formData.ticket_count > 500) {
        showSnackbar(
          `Обработка на ${formData.ticket_count} билета, моля изчакайте...`,
          "info"
        );
      }

      // Step 1: Generate user data in memory (no API calls yet)
      const userDataArray = [];
      const ticketNumbers = [];
      const guestIds = [];

      // Generate ticket numbers and user data
      for (let i = 0; i < formData.ticket_count; i++) {
        const ticketNumber = `${formData.home_team
          .substring(0, 3)
          .toUpperCase()}-${formData.away_team
          .substring(0, 3)
          .toUpperCase()}-${String(i + 1).padStart(4, "0")}`;
        const guestId = uuidv4().replace(/-/g, "");

        // Create user object
        const user = {
          name: `Посетител ${ticketNumber}`,
          email: `visitor_${ticketNumber.toLowerCase()}@example.com`,
          guest_id: guestId,
          start_date: dayjs(formData.event_date).format("YYYY-MM-DD HH:mm"),
          end_date: dayjs(formData.event_date)
            .add(formData.event_duration, "hour")
            .format("YYYY-MM-DD HH:mm"),
          loc_number: 1,
          room_name: formData.event_location,
          loc_name: "Спортна зала",
          sub_loc_name: "Посетител спортна зала",
          status: 3,
        };

        userDataArray.push(user);
        ticketNumbers.push(ticketNumber);
        guestIds.push(guestId);
      }

      // Step 2: Create users in chunks to avoid payload size limits
      const CHUNK_SIZE = 500; // Adjust based on your server limits
      const createdUsers = [];

      // Process users in chunks
      for (let i = 0; i < userDataArray.length; i += CHUNK_SIZE) {
        const chunk = userDataArray.slice(i, i + CHUNK_SIZE);
        const chunkTicketNumbers = ticketNumbers.slice(i, i + CHUNK_SIZE);
        const chunkGuestIds = guestIds.slice(i, i + CHUNK_SIZE);

        // Create chunk of users
        const response = await UserDataService.bulkCreate(chunk);

        // Map created users with their ticket numbers and guest IDs
        const chunkCreatedUsers = response.data.map((user, index) => ({
          sap: user.sap,
          guest_id: chunkGuestIds[index],
          ticket_number: chunkTicketNumbers[index],
        }));

        createdUsers.push(...chunkCreatedUsers);

        // Show progress for large batches
        if (userDataArray.length > CHUNK_SIZE) {
          const progress = Math.min(i + CHUNK_SIZE, userDataArray.length);
          showSnackbar(
            `Създадени ${progress} от ${userDataArray.length} потребители...`,
            "info"
          );
        }
      }

      // Step 3: Create the sports ticket event with the created users
      // For very large batches, we might need to chunk the users here too
      const MAX_USERS_PER_REQUEST = 1000;
      let sportsTicket = null;
      let devices = [];
      let floors = [];

      // Instead of distributing tickets evenly, we'll send all tickets to each device
      // Each device will get a complete copy of all tickets
      const deviceUserMap = selectedDevices.map(() => {
        // Each device gets all users
        return [...createdUsers];
      });

      // NOTE: The backend API (SportsTicketsService.create) needs to be updated to handle the 'devices' array
      // The API should store the relationship between the sports ticket and multiple devices

      // If we have a large number of users, create the ticket first with no users
      // then update it with user batches
      if (createdUsers.length > MAX_USERS_PER_REQUEST) {
        // Create ticket without users first
        const initialTicketData = {
          event_name: formData.event_name,
          home_team: formData.home_team,
          away_team: formData.away_team,
          event_date: dayjs(formData.event_date).format("YYYY-MM-DD HH:mm"),
          event_location: formData.event_location,
          ticket_count: formData.ticket_count,
          device_id: selectedDevices[0].id, // Use first device for the main record
          devices: selectedDevices.map((d) => d.id), // Store all device IDs
          users: [], // Empty initially
        };

        const initialResponse = await SportsTicketsService.create(
          initialTicketData
        );
        sportsTicket = initialResponse.data.sportsTicket;

        // Store device and floor information for each selected device
        for (const device of selectedDevices) {
          const deviceResponse = await DevicesDataService.get(device.id);
          devices.push(deviceResponse.data);
          floors.push(deviceResponse.data.floor);
        }

        // Now add users in chunks
        for (let i = 0; i < createdUsers.length; i += MAX_USERS_PER_REQUEST) {
          const userChunk = createdUsers.slice(i, i + MAX_USERS_PER_REQUEST);
          await SportsTicketsService.addUsers({
            ticketId: sportsTicket.id,
            users: userChunk,
          });

          // Show progress
          const progress = Math.min(
            i + MAX_USERS_PER_REQUEST,
            createdUsers.length
          );
          showSnackbar(
            `Добавени ${progress} от ${createdUsers.length} билета...`,
            "info"
          );
        }
      } else {
        // For smaller batches, create ticket with all users at once
        const ticketData = {
          event_name: formData.event_name,
          home_team: formData.home_team,
          away_team: formData.away_team,
          event_date: dayjs(formData.event_date).format("YYYY-MM-DD HH:mm"),
          event_location: formData.event_location,
          ticket_count: formData.ticket_count,
          device_id: selectedDevices[0].id, // Use first device for the main record
          devices: selectedDevices.map((d) => d.id), // Store all device IDs
          users: createdUsers,
        };

        const ticketResponse = await SportsTicketsService.create(ticketData);
        sportsTicket = ticketResponse.data.sportsTicket;

        // Store device and floor information for each selected device
        for (const device of selectedDevices) {
          const deviceResponse = await DevicesDataService.get(device.id);
          devices.push(deviceResponse.data);
          floors.push(deviceResponse.data.floor);
        }
      }

      // Step 4: Process device users and MQTT commands for each device
      showSnackbar(`Обработка на устройства и MQTT команди...`, "info");

      // Process each device with its assigned users
      for (
        let deviceIndex = 0;
        deviceIndex < selectedDevices.length;
        deviceIndex++
      ) {
        const deviceUsers = deviceUserMap[deviceIndex];
        const currentDevice = devices[deviceIndex];
        const currentFloor = floors[deviceIndex];

        if (!deviceUsers.length) continue; // Skip if no users assigned to this device

        showSnackbar(
          `Обработка на устройство ${currentDevice.name} (${deviceIndex + 1}/${
            selectedDevices.length
          }): добавяне на ${deviceUsers.length} билета...`,
          "info"
        );

        const DEVICE_CHUNK_SIZE = 50; // Reduced from 200 to 50 for better reliability

        // Process in chunks
        for (let i = 0; i < deviceUsers.length; i += DEVICE_CHUNK_SIZE) {
          const userChunk = deviceUsers.slice(i, i + DEVICE_CHUNK_SIZE);

          try {
            // Get free slots for this chunk
            const slotResponse = await MqttService.getFreeSlotsBatch(
              selectedDevices[deviceIndex].id,
              userChunk.length
            );
            const slots = slotResponse.slots || [];

            if (slots.length < userChunk.length) {
              showSnackbar(
                `Недостатъчно свободни слотове в устройството ${currentDevice.name} (${slots.length}/${userChunk.length})`,
                "error"
              );
              continue; // Skip this device but continue with others
            }

            // Create device users for this chunk
            const deviceUsersData = userChunk.map((user, index) => ({
              device_id: selectedDevices[deviceIndex].id,
              user_id: user.sap,
              slot: slots[index],
              template_id: null,
            }));

            await DeviceUsersService.bulkCreate(deviceUsersData);

            // Send MQTT insert commands for this chunk
            const insertCommands = userChunk.map((user, index) => ({
              device: currentDevice.mac,
              slot: slots[index],
              user: user.sap,
              pin: null,
              template: null,
              card: null,
              qr_code: user.guest_id,
            }));

            // Set a timeout for the MQTT operation
            const mqttPromise = MqttService.batchInsert(insertCommands);
            const timeoutPromise = new Promise(
              (_, reject) =>
                setTimeout(
                  () => reject(new Error("MQTT operation timed out")),
                  30000
                ) // 30 second timeout
            );

            // Race the MQTT operation against the timeout
            await Promise.race([mqttPromise, timeoutPromise]).catch((error) => {
              console.warn(
                `MQTT batch operation warning for device ${currentDevice.name}: ${error.message}. Continuing with next batch.`
              );
              // We'll continue even if there's a timeout, as the commands might still be processing
            });

            // Show progress
            const progress = Math.min(
              i + DEVICE_CHUNK_SIZE,
              deviceUsers.length
            );
            showSnackbar(
              `Устройство ${currentDevice.name}: обработени ${progress} от ${deviceUsers.length} билета...`,
              "info"
            );

            // Add a small delay between chunks to let the server breathe
            if (i + DEVICE_CHUNK_SIZE < deviceUsers.length) {
              await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
            }
          } catch (error) {
            console.error(
              `Error processing chunk ${i}-${i +
                DEVICE_CHUNK_SIZE} for device ${currentDevice.name}:`,
              error
            );
            showSnackbar(
              `Грешка при обработка на устройство ${
                currentDevice.name
              } за билети ${i}-${i + DEVICE_CHUNK_SIZE}. Продължаване...`,
              "warning"
            );
            // Continue with the next chunk even if this one fails
          }
        }
      }

      // Add the new ticket to the list
      setSportsTickets((prev) => [sportsTicket, ...prev]);

      // Reset form
      setFormData({
        event_name: "",
        home_team: "",
        away_team: "",
        event_date: new Date(),
        event_location: "",
        ticket_count: 10,
        selectedDevices: [],
        event_duration: 5, // Default duration in hours
      });

      showSnackbar(
        `${formData.ticket_count} билета са генерирани успешно и изпратени към ${selectedDevices.length} устройства`,
        "success"
      );
    } catch (error) {
      console.error("Error creating tickets:", error);
      showSnackbar("Грешка при генериране на билети", "error");
    }
  };

  // Delete dialog handlers
  const handleDeleteClick = async (ticketId) => {
    try {
      // Fetch ticket details to show in the confirmation dialog
      const ticketResponse = await SportsTicketsService.get(ticketId);
      const ticket = ticketResponse.data.sportsTicket;
      const individualTickets = ticketResponse.data.individualTickets || [];

      // Get unique devices this ticket is associated with
      const deviceIds = ticket.devices
        ? JSON.parse(ticket.devices)
        : [ticket.device_id];
      const deviceNames = [];

      // Fetch device names
      for (const deviceId of deviceIds) {
        try {
          const deviceResponse = await DevicesDataService.get(deviceId);
          deviceNames.push(deviceResponse.data.name);
        } catch (error) {
          console.error(
            `Error fetching device name for ID ${deviceId}:`,
            error
          );
          deviceNames.push(`Device ID: ${deviceId}`);
        }
      }

      setDeleteDialog({
        open: true,
        ticketId,
        ticketName: ticket.event_name,
        userCount: individualTickets.length,
        userSaps: individualTickets
          .map((ticket) => ticket.user_id)
          .slice(0, 10), // Show first 10 SAPs
        hasMoreUsers: individualTickets.length > 10,
        deviceNames,
      });
    } catch (error) {
      console.error("Error fetching ticket details for delete dialog:", error);
      // Fallback to basic dialog if fetch fails
      setDeleteDialog({
        open: true,
        ticketId,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      ticketId: null,
      ticketName: "",
      userCount: 0,
      userSaps: [],
      hasMoreUsers: false,
      deviceNames: [],
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      // First, get the ticket details to know which users to remove
      const ticketResponse = await SportsTicketsService.get(
        deleteDialog.ticketId
      );
      const ticket = ticketResponse.data.sportsTicket;
      const individualTickets = ticketResponse.data.individualTickets || [];

      showSnackbar(`Удаляване на билети и потребители...`, "info");

      // If we have individual tickets, remove only these specific users from all devices
      if (individualTickets.length > 0) {
        showSnackbar(
          `Премахване на ${individualTickets.length} потребители от всички устройства...`,
          "info"
        );

        // Get all user IDs from individual tickets - these are the ONLY users we want to remove
        const userIds = individualTickets.map((ticket) => ticket.user_id);

        console.log(
          `Removing ${userIds.length} specific users associated with event ID: ${deleteDialog.ticketId}`
        );

        // Process users in chunks to avoid overwhelming the system
        const CHUNK_SIZE = 50;
        for (let i = 0; i < userIds.length; i += CHUNK_SIZE) {
          const userChunk = userIds.slice(i, i + CHUNK_SIZE);

          // Process each user in the chunk - ONLY users associated with this event
          for (const userId of userChunk) {
            try {
              // Remove this specific user from all devices
              await MqttService.removeUserEverywhere(userId);

              // Add a small delay to prevent overwhelming the MQTT broker
              await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (error) {
              console.error(
                `Error removing user ${userId} from all devices:`,
                error
              );
              // Continue with next user even if this one fails
            }
          }

          // Show progress
          const progress = Math.min(i + CHUNK_SIZE, userIds.length);
          showSnackbar(
            `Премахнати ${progress} от ${userIds.length} потребители от всички устройства...`,
            "info"
          );

          // Add a small delay between chunks to let the server breathe
          if (i + CHUNK_SIZE < userIds.length) {
            await new Promise((resolve) => setTimeout(resolve, 1000)); // 1 second delay
          }
        }

        // After removing users from devices, also delete them from the database
        showSnackbar(
          `Изтриване на ${userIds.length} потребители от базата данни...`,
          "info"
        );

        // Delete users from the database in chunks
        for (let i = 0; i < userIds.length; i += CHUNK_SIZE) {
          const userChunk = userIds.slice(i, i + CHUNK_SIZE);

          try {
            // First, remove the device-user associations
            await DeviceUsersService.bulkDeleteByUserIds(userChunk);

            // Then delete the actual users
            await UserDataService.bulkDelete(userChunk);

            // Show progress
            const progress = Math.min(i + CHUNK_SIZE, userIds.length);
            showSnackbar(
              `Изтрити ${progress} от ${userIds.length} потребители от базата данни...`,
              "info"
            );
          } catch (error) {
            console.error(
              `Error deleting users ${userChunk.join(", ")} from database:`,
              error
            );
            // Continue with next chunk even if this one fails
          }

          // Add a small delay between chunks
          if (i + CHUNK_SIZE < userIds.length) {
            await new Promise((resolve) => setTimeout(resolve, 500)); // 0.5 second delay
          }
        }
      }

      // Finally, delete the ticket itself - this should also clean up the database entries
      await SportsTicketsService.delete(deleteDialog.ticketId);

      // Remove the deleted ticket from the list
      setSportsTickets((prev) =>
        prev.filter((ticket) => ticket.id !== deleteDialog.ticketId)
      );

      showSnackbar("Билетите и потребителите са изтрити успешно", "success");
    } catch (error) {
      console.error("Error deleting tickets:", error);
      showSnackbar("Грешка при изтриване на билети и потребители", "error");
    }
    handleDeleteCancel();
  };

  // View dialog handlers
  const handleViewClick = async (ticketId) => {
    try {
      // First, open the modal with basic event data
      const basicResponse = await SportsTicketsService.get(ticketId);

      // Extract the sports ticket data but set individualTickets to null initially
      const ticketData = {
        sportsTicket: basicResponse.data.sportsTicket,
        totalTickets:
          (basicResponse.data.individualTickets &&
            basicResponse.data.individualTickets.length) ||
          0,
        individualTickets: null, // Will be loaded asynchronously
      };

      // Open the modal with the basic data
      setViewDialog({
        open: true,
        ticketData: ticketData,
      });

      // Then load the individual tickets asynchronously
      // This allows the modal to open quickly while tickets are still loading
      setTimeout(async () => {
        try {
          // Fetch the complete data with individual tickets
          const fullResponse = await SportsTicketsService.get(ticketId);

          // Update the modal with the complete data
          setViewDialog((prev) => ({
            ...prev,
            ticketData: {
              ...prev.ticketData,
              individualTickets: fullResponse.data.individualTickets,
            },
          }));
        } catch (error) {
          console.error("Error fetching individual tickets:", error);
          showSnackbar("Грешка при зареждане на индивидуални билети", "error");
        }
      }, 100); // Small delay to ensure modal opens first
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      showSnackbar("Грешка при зареждане на детайли за билети", "error");
    }
  };

  const handleViewClose = () => {
    setViewDialog({
      open: false,
      ticketData: null,
    });
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBarBasic />
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {/* Header Section */}
        <Container maxWidth="lg" className={classes.container}>
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
          <Grid container className={classes.header}>
            <Grid item xs={12}>
              <Typography variant="h4" component="h1" align="center">
                Генериране на билети за спортни събития
              </Typography>
              <Typography variant="body1" align="center" color="textSecondary">
                Създайте билети с QR кодове за достъп до спортни събития
              </Typography>
            </Grid>
          </Grid>

          {/* Main Content */}
          <Grid container spacing={2}>
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography variant="h6" gutterBottom>
                  Създаване на билети
                </Typography>
                <Divider />
                <form onSubmit={handleSubmit} className={classes.form}>
                  <TextField
                    label="Име на събитието"
                    variant="outlined"
                    value={formData.event_name}
                    onChange={(e) =>
                      setFormData({ ...formData, event_name: e.target.value })
                    }
                    required
                    size="small"
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Отбор домакин"
                        variant="outlined"
                        value={formData.home_team}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            home_team: e.target.value,
                          })
                        }
                        required
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Гостуващ отбор"
                        variant="outlined"
                        value={formData.away_team}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            away_team: e.target.value,
                          })
                        }
                        required
                        size="small"
                        fullWidth
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    label="Локация на събитието"
                    variant="outlined"
                    value={formData.event_location}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event_location: e.target.value,
                      })
                    }
                    required
                    size="small"
                  />

                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDateTimePicker
                      variant="inline"
                      inputVariant="outlined"
                      label="Дата и час на събитието"
                      value={formData.event_date}
                      onChange={(date) =>
                        setFormData({ ...formData, event_date: date })
                      }
                      format="dd/MM/yyyy HH:mm"
                      ampm={false}
                      className={classes.datePicker}
                      required
                      size="small"
                    />
                  </MuiPickersUtilsProvider>

                  <TextField
                    label="Продължителност (часове)"
                    variant="outlined"
                    type="number"
                    value={formData.event_duration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event_duration: parseInt(e.target.value) || 5,
                      })
                    }
                    required
                    size="small"
                    inputProps={{ min: 1, max: 24 }}
                  />

                  <TextField
                    label="Брой билети"
                    variant="outlined"
                    type="number"
                    value={formData.ticket_count}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ticket_count: parseInt(e.target.value) || 0,
                      })
                    }
                    required
                    size="small"
                    inputProps={{ min: 1, max: 10000 }}
                    helperText="Всички билети ще бъдат изпратени към всяко избрано устройство"
                  />

                  <Autocomplete
                    id="device-selector"
                    options={devices}
                    getOptionLabel={(option) => option.name || ""}
                    value={formData.selectedDevices}
                    size="small"
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(_, newValue) => {
                      console.log("Selected devices:", newValue);
                      // Force it to be an array
                      const updatedDevices = newValue ? [...newValue] : [];
                      console.log(
                        "Updated devices (forced array):",
                        updatedDevices
                      );
                      setFormData({
                        ...formData,
                        selectedDevices: updatedDevices,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Изберете устройства за достъп"
                        variant="outlined"
                        error={formData.selectedDevices.length === 0}
                        helperText={
                          formData.selectedDevices.length === 0
                            ? "Моля, изберете поне едно устройство"
                            : `Избрани устройства: ${formData.selectedDevices.length}. Можете да изберете няколко устройства.`
                        }
                      />
                    )}
                  />

                  {/* Debug button to manually add a device */}
                  {devices.length > 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      style={{ marginTop: "8px" }}
                      onClick={() => {
                        const firstDevice = devices[0];
                        console.log("Manually adding device:", firstDevice);
                        setFormData({
                          ...formData,
                          selectedDevices: [firstDevice],
                        });
                      }}
                    >
                      Добави първото устройство (Debug)
                    </Button>
                  )}

                  {formData.selectedDevices.length > 0 &&
                    formData.ticket_count > 0 && (
                      <Paper
                        elevation={0}
                        style={{
                          padding: "8px",
                          backgroundColor: "#f5f5f5",
                          marginTop: "8px",
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          Разпределение на билетите:
                        </Typography>
                        <Typography variant="body2">
                          {`Общо ${formData.ticket_count} билета ще бъдат изпратени към всяко избрано устройство.`}
                        </Typography>
                        {formData.selectedDevices.map((device) => (
                          <Typography
                            key={device.id}
                            variant="body2"
                            style={{ marginLeft: "8px" }}
                          >
                            {`• ${device.name}: ${formData.ticket_count} билета`}
                          </Typography>
                        ))}
                      </Paper>
                    )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.submitButton}
                  >
                    Генериране на билети
                  </Button>
                </form>
              </Paper>
            </Grid>

            {/* Tickets List Section */}
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography variant="h6" gutterBottom>
                  Генерирани събития
                </Typography>
                <Divider />
                <List className={classes.ticketsList} disablePadding>
                  {sportsTickets.map((ticket) => (
                    <ListItem key={ticket.id} className={classes.listItem}>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            style={{ display: "inline-block", width: "100%" }}
                          >
                            {ticket.event_name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            variant="body2"
                            color="textSecondary"
                            style={{ display: "inline-block", width: "100%" }}
                          >
                            {`${ticket.home_team} vs ${
                              ticket.away_team
                            } - ${dayjs(ticket.event_date).format(
                              "DD/MM/YYYY HH:mm"
                            )}`}
                            <br />
                            {`${ticket.ticket_count} билета`}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <BlackTooltip
                          title="Преглед на билети"
                          arrow
                          placement="left"
                        >
                          <IconButton
                            edge="end"
                            aria-label="view"
                            className={classes.viewButton}
                            onClick={() => handleViewClick(ticket.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </BlackTooltip>
                        <BlackTooltip
                          title="Изтриване на билети"
                          arrow
                          placement="left"
                        >
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            className={classes.deleteButton}
                            onClick={() => handleDeleteClick(ticket.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </BlackTooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
          <DialogTitle style={{ paddingBottom: 0 }}>
            Потвърждение за изтриване
          </DialogTitle>
          <DialogContent style={{ paddingTop: "8px" }}>
            <Typography paragraph>
              Сигурни ли сте, че искате да изтриете билетите за събитие{" "}
              <strong>{deleteDialog.ticketName}</strong>? Това действие ще
              премахне всички генерирани QR кодове и потребители.
            </Typography>

            <Typography paragraph style={{ color: "red" }}>
              <strong>Внимание:</strong> Това действие ще премахне
              потребителите, свързани с това конкретно събитие, от всички
              устройства и от базата данни. Този процес е необратим и може да
              отнеме известно време.
            </Typography>

            {deleteDialog.userCount > 0 && (
              <>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: "bold", marginTop: "16px" }}
                >
                  Детайли за изтриване:
                </Typography>
                <Typography variant="body2">
                  • Брой потребители за изтриване:{" "}
                  <strong>{deleteDialog.userCount}</strong>
                </Typography>

                {deleteDialog.deviceNames &&
                  deleteDialog.deviceNames.length > 0 && (
                    <Typography variant="body2">
                      • Устройства, от които ще бъдат премахнати:{" "}
                      <strong>{deleteDialog.deviceNames.join(", ")}</strong>
                    </Typography>
                  )}

                <Typography
                  variant="body2"
                  style={{ marginTop: "8px", color: "#d32f2f" }}
                >
                  • Процес на изтриване:
                </Typography>
                <ol
                  style={{
                    margin: "4px 0 8px 20px",
                    padding: 0,
                    color: "#d32f2f",
                  }}
                >
                  <li>Премахване на потребителите от всички устройства</li>
                  <li>
                    Изтриване на връзките между потребителите и устройствата от
                    базата данни
                  </li>
                  <li>Изтриване на самите потребители от базата данни</li>
                  <li>Изтриване на билетите и събитието</li>
                </ol>

                {deleteDialog.userSaps && deleteDialog.userSaps.length > 0 && (
                  <>
                    <Typography variant="body2" style={{ marginTop: "8px" }}>
                      • Първите {Math.min(10, deleteDialog.userSaps.length)} SAP
                      номера на потребители:
                    </Typography>
                    <Paper
                      elevation={0}
                      style={{
                        backgroundColor: "#f5f5f5",
                        padding: "8px",
                        maxHeight: "100px",
                        overflow: "auto",
                        marginTop: "4px",
                      }}
                    >
                      {deleteDialog.userSaps.map((sap, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          style={{ fontFamily: "monospace" }}
                        >
                          {sap}
                        </Typography>
                      ))}
                      {deleteDialog.hasMoreUsers && (
                        <Typography
                          variant="body2"
                          style={{ fontStyle: "italic", marginTop: "4px" }}
                        >
                          ... и още {deleteDialog.userCount - 10} потребители
                        </Typography>
                      )}
                    </Paper>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteConfirm}
              color="primary"
              variant="contained"
            >
              Да, изтрий всичко
            </Button>
            <Button onClick={handleDeleteCancel} color="primary">
              Отказ
            </Button>
          </DialogActions>
        </Dialog>

        {/* Use the extracted ViewTicketsDialog component */}
        <ViewTicketsDialog
          open={viewDialog.open}
          onClose={handleViewClose}
          ticketData={viewDialog.ticketData}
          classes={classes}
        />
      </main>
    </div>
  );
};

export default SportsTicketsPage;
